import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Typography, IconButton } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import IconBack from '@material-ui/icons/ArrowBack'
import cn from 'classnames'
import withPromise from 'helpers/withPromise'
import { APP_LANGUAGES } from 'constants/index'
import {
  changeTask,
  fetchTaskFormData,
  taskClearFormData,
} from 'reducers/jobTasksAdmin/reducer'
import { patchJobWorkflowInputs as taskPreviewInputsChange } from 'reducers/jobInputsAdmin/reducer'
import TaskForm from './TaskForm'
import messages from './translate'
import AssignUsersFormTable from './AssignUsersFormTable'

function TaskItem({
  taskPreviewInputsChange,
  taskClearFormData,
  fetchTaskFormData,
  changeTask,
  formDataObject,
  assignData,
  match: {
    params: { id },
  },
  history: { goBack },
  location: { search },
}) {
  const formData = formDataObject.toJS()
  const getEntityId = () => (id !== 'create' ? id : false)
  let taskFormData = { ...formData }
  const [isUserTableOpen, setIsUserTableOpen] = useState(false)
  const [selected, setSelected] = useState([])
  const [isBulkSelect, setIsBulkSelect] = useState(false)
  const [userAssignInternalList, setUserAssignInternalList] = useState([])

  useEffect(() => {
    if (formData.assigned_users) {
      setSelected([
        ...formData.assigned_users.map(user => ({ ...user, isUser: true })),
        ...formData.assigned_user_groups,
      ])
    }
  }, [formDataObject])

  useEffect(() => {
    fetchTaskFormData({ entityId: getEntityId() })
  }, [])

  useEffect(
    () => () => {
      taskClearFormData()
    },
    [],
  )

  const onSubmit = (
    {
      position,
      title,
      instructions,
      conditional_logic_items,
      populate_logic_items,
      preview_inputs,
      ...rest
    },
    imagePath,
    previewInputs,
  ) => {
    const newInstructions = instructions
    const newTitle = title

    Object.values(APP_LANGUAGES).forEach(({ key }) => {
      if (!title[key]) {
        newTitle[key] = title.en
      }

      if (instructions.en && !instructions[key]) {
        newInstructions[key] = instructions.en
      }

      if (!instructions.en) {
        newInstructions[key] = ''
      }
    })

    const formData = {
      position: Number(position),
      title: newTitle,
      instructions: newInstructions,
      ...rest,
      siteAssignment: isBulkSelect ? 'bulk' : 'individual',
      assigned_users: isBulkSelect
        ? selected.filter(({ isUser }) => isUser).map(({ id }) => id)
        : [],
      assigned_user_groups: isBulkSelect
        ? selected.filter(({ isUser }) => !isUser).map(({ id }) => id)
        : [],
    }

    if (!isBulkSelect) {
      if (userAssignInternalList && userAssignInternalList.length) {
        const userAssignList = []

        for (const userAssign of userAssignInternalList) {
          const siteId = userAssign.site?.id ?? null
          const users = userAssign.users.map(({ id: userId }) => userId)
          const userGroups = userAssign.userGroups.map(
            ({ id: userGroupsId }) => userGroupsId,
          )
          userAssignList.push({ sites: [siteId], users, userGroups })
        }

        formData.individual_site_assignments = userAssignList
      }
    } else {
      formData.individual_site_assignments = []
    }

    const entityId = getEntityId()

    if (!entityId) {
      if (imagePath) {
        formData.image_path = imagePath
      }
    }

    taskPreviewInputsChange(previewInputs)

    if (entityId) {
      const mapConditions = conditions =>
        conditions.map(({ criteria, input, values }) => ({
          criteria,
          input,
          values: Array.isArray(values) ? values : Object.keys(values),
        }))

      formData.conditional_logic = (conditional_logic_items || []).map(
        condBlock => ({
          uuid: condBlock.uuid,
          operator: condBlock.operator,
          conditions: mapConditions(condBlock.conditions),
          ...(condBlock.inputs?.length
            ? {
                inputs: condBlock.inputs.map(input => input.id || input),
              }
            : {}),
          ...(condBlock.input_groups?.length
            ? {
                input_groups: condBlock.input_groups.map(
                  inputGroup => inputGroup.id || inputGroup,
                ),
              }
            : {}),
        }),
      )

      formData.populate_logic = (populate_logic_items || []).map(
        populateBlock => {
          const conditions = mapConditions(populateBlock.conditions)

          return {
            uuid: populateBlock.uuid,
            trigger_input:
              populateBlock.trigger_input?.id ||
              populateBlock.trigger_input ||
              conditions[0]?.input,
            inputs: (populateBlock.inputs || [])
              .map(input => (typeof input === 'object' ? input.id : input))
              .filter(Boolean),
            operator: populateBlock.operator || 'and',
            conditions,
            population_values: populateBlock.population_values,
          }
        },
      )
    }

    return withPromise(changeTask, {
      entityId,
      values: formData,
    }).then(errors => (errors ? Promise.resolve(errors) : goBack()))
  }

  if (search && search.length) {
    const queryParams = new URLSearchParams(search)

    taskFormData = {
      workflow: {
        id: +queryParams.get('id'),
      },
    }
  }

  return (
    <>
      <div
        className={cn('container-fluid no-breadcrumb', {
          hidden: isUserTableOpen,
        })}>
        <Typography variant="h5" className="headline">
          <IconButton size="medium" className="btn-back" onClick={goBack}>
            <IconBack fontSize="small" />
          </IconButton>
          <FormattedMessage {...messages.jobWorkflowTaskInfo} />
        </Typography>
        <TaskForm
          selected={selected}
          setSelected={setSelected}
          formData={taskFormData}
          isBulkSelect={isBulkSelect}
          setIsBulkSelect={setIsBulkSelect}
          onSubmit={onSubmit}
          setIsUserTableOpen={setIsUserTableOpen}
        />
      </div>
      <AssignUsersFormTable
        isUserTableOpen={isUserTableOpen}
        userAssignInternalList={userAssignInternalList}
        setUserAssignInternalList={setUserAssignInternalList}
        setIsUserTableOpen={setIsUserTableOpen}
        assignData={assignData}
        id={formData.id}
      />
    </>
  )
}

TaskItem.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  formData: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  changeTask: PropTypes.func,
  taskClearFormData: PropTypes.func,
  fetchTaskFormData: PropTypes.func,
}

TaskItem.defaultProps = {
  fetchTaskFormData: () => {},
  taskClearFormData: () => {},
  changeTask: () => {},
}

export default connect(
  state => ({
    formDataObject: state.getIn(['jobTasksAdmin', 'formData']),
    loading: state.getIn(['jobTasksAdmin', 'loading']),
    assignData: state.getIn(['myJobs', 'assignData']).toJS(),
  }),
  {
    taskPreviewInputsChange,
    fetchTaskFormData,
    changeTask,
    taskClearFormData,
  },
)(TaskItem)
